import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Alert, Box, CircularProgress } from "@mui/material";
import PrimaryButton from "./Button/PrimaryButton";
import PrimaryTextField from "./PrimaryTextField/PrimaryTextField";
import { useAuth } from "../Hooks/useAuth";
import { useContext } from "react";
import { GlobalStates } from "../GlobalStates";
import { supabase } from "../services/supabaseClient";

// Simple function to check if a string is empty
const isEmpty = (str) => {
  return !str || str.trim() === "";
};

const Prisijungimas = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login, loading } = useAuth();
  const { setUser, setToken } = useContext(GlobalStates);

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  // Handle login
  const handleLogin = async () => {
    try {
      setLoginError("");

      // Check for recent password change
      const wasPasswordRecentlyChanged =
        localStorage.getItem("password_recently_changed") === "true";

      if (wasPasswordRecentlyChanged) {
        // Clear localStorage
        for (let key in localStorage) {
          if (
            localStorage.hasOwnProperty(key) &&
            key !== "i18nextLng" &&
            key !== "password_recently_changed"
          ) {
            localStorage.removeItem(key);
          }
        }

        // Clear Supabase session
        await supabase.auth.signOut();

        // Clear cookies
        document.cookie.split(";").forEach((cookie) => {
          const name = cookie.trim().split("=")[0];
          if (name.includes("Formu") || name.includes("supabase")) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          }
        });

        // Wait for session cleanup
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      // Validate required fields
      if (isEmpty(email) || isEmpty(password)) {
        setLoginError(t("privalomi_laukai"));
        return;
      }

      // Attempt login
      const result = await login(email, password);

      if (!result.success) {
        // Check for specific error types
        if (result.error?.message?.includes('Invalid login credentials')) {
          setLoginError(t("login_error"));
        } else if (result.error?.message?.includes('Email not confirmed')) {
          setLoginError(t("email_not_confirmed_error"));
        } else {
          setLoginError(t("login_error"));
        }
        return;
      }

      try {
        // Get user data
        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("*")
          .eq("id", result.session.user.id)
          .single();

        if (userError) throw new Error(t("prisijungimo_klaida"));

        // Check if user is confirmed
        if (!userData.confirmed) {
          await supabase.auth.signOut();
          setLoginError(t("accComfirmedError"));
          // Clear tokens to ensure no access
          setUser(null);
          setToken('');
          return;
        }

        // Check if subscription is valid
        const isValidSubscription = userData.valid_to && new Date(userData.valid_to) >= new Date();
        if (!isValidSubscription) {
          await supabase.auth.signOut();
          setLoginError(t("accValidToError"));
          // Clear tokens to ensure no access
          setUser(null);
          setToken('');
          return;
        }

        // Clean up password reset related data
        localStorage.removeItem("password_recently_changed");
        localStorage.removeItem("password_reset_success");
        sessionStorage.removeItem("password_reset_in_progress");
        sessionStorage.removeItem("password_reset_timeout");

        // Update user data with session info
        const fullUserData = {
          ...userData,
          ...result.session.user,
          access_token: result.session.access_token,
        };

        // Set user and token
        setUser(fullUserData);
        setToken(result.session.access_token);

        // Redirect to dashboard
        navigate("/");
      } catch (error) {
        await supabase.auth.signOut();
        throw error;
      }
    } catch (error) {
      console.error("Login error:", error);
      setLoginError(error.message || t("sistemos_klaida"));
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !isEmpty(email) && !isEmpty(password)) {
      handleLogin();
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Form fields */}
      <PrimaryTextField
        id="prisijungimas-email"
        label="login_email"
        onChange={setEmail}
        fullWidth={true}
        size="small"
        classNameForm="txtFielfContainer"
        autoComplete="email"
        onKeyUp={handleKeyPress}
        required
      />

      <PrimaryTextField
        id="prisijungimas-password"
        label="login_pass"
        onChange={setPassword}
        type="password"
        fullWidth={true}
        size="small"
        classNameForm="txtFielfContainer"
        autoComplete="current-password"
        onKeyUp={handleKeyPress}
        required
      />

      {/* Login messages */}
      {loginError && (
        <>
          <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
            {loginError === "show" ? t("login_error") : loginError}
          </Alert>
          {loginError === t("login_error") && (
            <Alert severity="info" sx={{ mb: 1 }}>
              {t("db_migration_info")}
            </Alert>
          )}
        </>
      )}

      {/* Login button */}
      <div className="submitButtonCont">
        <PrimaryButton
          onClick={handleLogin}
          startIcon={
            loading ? (
              <CircularProgress size={20} sx={{ ml: 1 }} color="inherit" />
            ) : null
          }
          text={loading ? t("jungiamasi") : t("prisijungti")}
          className="mobLogIn"
          disabled={isEmpty(email) || isEmpty(password) || loading}
        />

        <PrimaryButton
          text="pamirsai_pass"
          sx={{
            boxShadow: 0,
            ":hover": {
              boxShadow: "0",
              backgroundColor: "white",
            },
          }}
          onClick={() => navigate("/PrimintiSlaptazodi")}
          className="pamirsai_pass"
          variant="text"
        />
      </div>
    </Box>
  );
};

export default Prisijungimas;
